import { createApi } from '@reduxjs/toolkit/query/react';
import { createBackendBaseQuery, transformApiError } from '../../utils/createAuthBaseQuery';
import { API_URLS } from '../../constants/apiUrl';
import { ElectricalData, MechanicalData } from './motorInvestigationComparisonApiSlice';

export interface DropdownOption {
  text: string;
  value: string;
}

export interface MotorMasterData {
  mountingType: DropdownOption[];
  speedType: DropdownOption[];
  motorType: DropdownOption[];
  motorRegion: DropdownOption[];
}

export interface ExternalInvestigationData {
  alternativeVoltages: string;
  motorRegionId: number;
  motorTypeId: number;
  motorSpeedTypeId: number;
  motorMountingTypeId: number;
  mechanicalPartNumber: number;
  electricalPartNumber: number;
  newMotorPartNumber: number;
}

export interface ExternalInvestigationRequest {
  motorInvestigationFlowId: number;
  motorInvestigationTicketId: number;
  motorRegionId: number;
  motorTypeId: number;
  motorSpeedTypeId: number;
  motorMountingTypeId: number;
  mechanicalPartNumber: string;
  electricalPartNumber: string;
  newMotorPartNumber: number;
}

export interface ExternalInvestigationAlternative {
  motorInvestigationFlowId: number;
  voltage: number;
  mechanicalPartNumber: string;
  electricalPartNumber: string;
  motorPn: number;
  oldMotorId: number;
  oldElectricalData: ElectricalData;
  newElectricalData: ElectricalData;
}
export interface ExternalInvestigationAlternativeMotorResponse {
  electricalData: ElectricalData;
  mechanicalData: MechanicalData;
}
export interface ExternalInvestigationAlternativeResponse {
  oldMotor: ExternalInvestigationAlternativeMotorResponse;
  newMotor: ExternalInvestigationAlternativeMotorResponse;
}
const BASE_URL = process.env.REACT_APP_MOTER_SERVICE_API_BASE_URL as string;

if (!BASE_URL) {
  throw new Error('REACT_APP_MOTER_SERVICE_API_BASE_URL environment variable is not set');
}

const baseQuery = createBackendBaseQuery(BASE_URL);

export const motorExternalInvestigationApiSlice = createApi({
  reducerPath: 'motorExternalInvestigationApi',
  baseQuery: baseQuery,
  tagTypes: ['MotorMasterData', 'ExternalInvestigationData', 'ExternalInvestigationAlternativeData'],
  endpoints(builders) {
    return {
      fetchExternalInvestigationData: builders.query<ExternalInvestigationData, { investigationFlowId: number; investigationTicketId: number }>({
        query: ({ investigationFlowId, investigationTicketId }) => ({
          url: `${API_URLS.MOTOR_INVESTIGATION.EXTERNAL_INVESTIGATION_DATA}?motorInvestigationFlowId=${investigationFlowId}&motorInvestigationTicketId=${investigationTicketId}`,
          method: 'GET',
        }),
        providesTags: ['ExternalInvestigationData'],
        transformErrorResponse: transformApiError,
      }),
     saveExternalInvestigationData: builders.mutation<number, { externalInvestigationData: ExternalInvestigationRequest }>({
           query({ externalInvestigationData }) {
             return {
             url: API_URLS.MOTOR_INVESTIGATION.SAVE_EXTERNAL_INVESTIGATION_DATA,
             method: 'POST',
             body: externalInvestigationData,
             };
           },
           invalidatesTags: [],
           transformErrorResponse: transformApiError,
      }),
      saveExternalInvestigationAlternativeData: builders.mutation<string, { externalInvestigationAlternativeData: ExternalInvestigationAlternative }>({
           query({ externalInvestigationAlternativeData }) {
             return {
             url: API_URLS.MOTOR_INVESTIGATION.SAVE_EXTERNAL_INVESTIGATION_ALTERNATIVE_DATA,
             method: 'POST',
             body: externalInvestigationAlternativeData,
             };
           },
           invalidatesTags: [],
           transformErrorResponse: transformApiError,
      }),
      fetchExternalInvestigationElectricalAlternativeData: builders.query<ExternalInvestigationAlternativeResponse, { motorInvestigationFlowAlternativeId: number; oldMotorNumber: number;voltage: number, frequency: number, motorSpeedTypeId: number }>({
        query: ({ motorInvestigationFlowAlternativeId, oldMotorNumber, voltage, frequency, motorSpeedTypeId }) => ({
          url: `${API_URLS.MOTOR_INVESTIGATION.EXTERNAL_INVESTIGATION__ELECTRICAL_ALTERNATIVE_DATA}?motorInvestigationFlowAlternativeId=${motorInvestigationFlowAlternativeId}&oldMotorNumber=${oldMotorNumber}&voltage=${voltage}&frequency=${frequency}&motorSpeedTypeId=${motorSpeedTypeId}`,
          method: 'GET',
        }),
        providesTags: ['ExternalInvestigationAlternativeData'],
        transformErrorResponse: transformApiError,
      }),
      fetchExternalInvestigationMechanicalAlternativeData: builders.query<ExternalInvestigationAlternativeResponse, { motorInvestigationFlowAlternativeId: number; oldMotorNumber: number;voltage: number, motorSpeedTypeId: number }>({
        query: ({ motorInvestigationFlowAlternativeId, oldMotorNumber, voltage, motorSpeedTypeId }) => ({
          url: `${API_URLS.MOTOR_INVESTIGATION.EXTERNAL_INVESTIGATION__MECHANICAL_ALTERNATIVE_DATA}?motorInvestigationFlowAlternativeId=${motorInvestigationFlowAlternativeId}&oldMotorNumber=${oldMotorNumber}&voltage=${voltage}&motorSpeedTypeId=${motorSpeedTypeId}`,
          method: 'GET',
        }),
        providesTags: ['ExternalInvestigationAlternativeData'],
        transformErrorResponse: transformApiError,
      }),
      deleteExternalInvestigationAlternativeData: builders.mutation<any, number>({
           query(id) {
             return {
             url: `${API_URLS.MOTOR_INVESTIGATION.DELETE_EXTERNAL_INVESTIGATION_ALTERNATIVE_DATA}?externalInvestigationAlternativeId=${id}`,
             method: 'POST',
             };
           },
           invalidatesTags: [
            'ExternalInvestigationData',
            'ExternalInvestigationAlternativeData'
          ],
           transformErrorResponse: transformApiError,
      }),
    };
  },
});

export const {
  useFetchExternalInvestigationDataQuery,
  useLazyFetchExternalInvestigationDataQuery,
  useFetchExternalInvestigationElectricalAlternativeDataQuery, 
  useLazyFetchExternalInvestigationElectricalAlternativeDataQuery,
  useFetchExternalInvestigationMechanicalAlternativeDataQuery,
  useLazyFetchExternalInvestigationMechanicalAlternativeDataQuery,
  useSaveExternalInvestigationDataMutation,
  useSaveExternalInvestigationAlternativeDataMutation,
  useDeleteExternalInvestigationAlternativeDataMutation
} = motorExternalInvestigationApiSlice;
